import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select'
import { toast } from '@/hooks/use-toast'
import { useCreateWorkflow } from '@/features/admin/workflowsQueries'
import { WORKFLOW_TYPES } from '@/types/admin'
import type { ApiRequestError } from '@/services/api'

const schema = z.object({
  name: z.string().min(1, 'Name is required'),
  type: z.enum(['oc', 'so']),
  audience: z.enum(['test', 'customer']),
})

type FormValues = z.infer<typeof schema>

interface CreateWorkflowDialogProps {
  organizationId: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function CreateWorkflowDialog({ organizationId, open, onOpenChange }: CreateWorkflowDialogProps) {
  const createWorkflow = useCreateWorkflow(organizationId)
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { name: '', type: 'oc', audience: 'test' },
  })

  function close() {
    reset()
    onOpenChange(false)
  }

  const onSubmit = handleSubmit(async (values) => {
    try {
      await createWorkflow.mutateAsync(values)
      toast({ title: 'Workflow created' })
      close()
    } catch (error) {
      toast({
        variant: 'destructive',
        description: (error as ApiRequestError).message ?? 'Could not create workflow',
      })
    }
  })

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create workflow</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700" htmlFor="workflow-name">
              Name
            </label>
            <Input id="workflow-name" {...register('name')} />
            {errors.name && <p className="text-xs text-red-600">{errors.name.message}</p>}
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">Type</label>
            <Select value={watch('type')} onValueChange={(v) => setValue('type', v as FormValues['type'])}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {WORKFLOW_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">Audience</label>
            <Select
              value={watch('audience')}
              onValueChange={(v) => setValue('audience', v as FormValues['audience'])}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="test">Test</SelectItem>
                <SelectItem value="customer">Customer</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={close}>
              Cancel
            </Button>
            <Button type="submit" disabled={createWorkflow.isPending}>
              {createWorkflow.isPending ? 'Creating…' : 'Create workflow'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
